import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, ClipboardText, CheckCircle } from "@phosphor-icons/react";

/**
 * Dialog shown when receiving incoming roll(s). Asks whether the roll(s) are an
 * inventory restock (added to active stock) or a purchase against a placed order
 * (consumed on arrival → marked depleted and tagged with the order reference).
 */
export default function ReceiveRollDialog({ open, onOpenChange, count = 1, submitting = false, onConfirm }) {
  const [mode, setMode] = useState("stock");
  const [orderRef, setOrderRef] = useState("");

  useEffect(() => {
    if (open) { setMode("stock"); setOrderRef(""); }
  }, [open]);

  const isOrder = mode === "order";
  const canConfirm = !submitting && (!isOrder || orderRef.trim().length > 0);
  const label = count === 1 ? "roll" : `${count} rolls`;

  const options = [
    {
      key: "stock",
      icon: Package,
      title: "Inventory restock",
      desc: "Add to active stock at full length so it's available for cutting.",
    },
    {
      key: "order",
      icon: ClipboardText,
      title: "Purchase on order",
      desc: "Allocated to a placed order — consumed on arrival and marked depleted.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive {label}</DialogTitle>
          <DialogDescription>Is this an inventory restock, or a purchase against a placed order?</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          {options.map((o) => {
            const Icon = o.icon;
            const active = mode === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setMode(o.key)}
                data-testid={`receive-mode-${o.key}`}
                className={`w-full text-left flex items-start gap-3 rounded-md border p-3 transition-colors ${active ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500" : "border-slate-200 hover:bg-slate-50"}`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} className={active ? "text-blue-600 mt-0.5" : "text-slate-500 mt-0.5"} />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{o.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {isOrder && (
          <div className="space-y-1.5">
            <Label htmlFor="order-ref" className="text-xs">Order reference</Label>
            <Input
              id="order-ref"
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
              placeholder="e.g. customer name / job or order number"
              data-testid="receive-order-ref"
              autoFocus
            />
            <p className="text-[11px] text-slate-400">Required — this label is stored with the roll in the Archive.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="receive-cancel-btn">Cancel</Button>
          <Button
            onClick={() => onConfirm(mode, orderRef.trim())}
            disabled={!canConfirm}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-md"
            data-testid="receive-confirm-btn"
          >
            <CheckCircle size={16} weight="fill" />
            {submitting ? "Receiving…" : isOrder ? "Allocate & receive" : "Receive to stock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
